import { accountReloadSuppressed } from '@/utils/wallet/account-change-guard'
import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_PUBLIC_KEY,
	STORAGE_WALLET_TYPE,
	NETWORK,
	STORAGE_XVERSE_TAPROOT_PUBLIC_KEY,
	STORAGE_XVERSE_PAYMENT_ADDRESS
} from '@/utils/constants'
import SafeLocalStorage from '../safe-local-storage'
import * as btc from '@scure/btc-signer'
import { allInputIndexes, dummyNativeSegwitPsbt, extractFinalizedPsbtHex, inputScriptHex, normalizePsbtHex, parsePsbtTransaction } from './psbt'
import Wallet from './'
import { WalletProvider } from '@/utils/types/wallet'

import { isUserRejection } from './signing-errors'

export default class OYLWallet implements WalletProvider {
	get name(): string {
		return 'OYL'
	}

	get capabilities(): string[] {
		return ['payments-wallet']
	}

	get connectIcon(): string {
		return '/connect-oyl.png'
	}
	get enabled(): boolean {
		return NETWORK === 'BTC'
	}

	async connect(): Promise<boolean> {
		// @ts-ignore
		if (!window.oyl) {
			window.open('https://oyl.io/', '_blank')
			return false
		}

		await this.login()
		return true
	}

	async listenForAccountChange(): Promise<void> {
		// @ts-ignore
		if (!window.oyl || Wallet.type() !== 'oyl') {
			return
		}

		// @ts-ignore
		window.oyl.on('accountsChanged', async () => {
			await this.login()
			if (accountReloadSuppressed()) return
			window.location.reload()
		})
	}

	async dummyPsbt(inscription_id: string): Promise<string> {
		const psbt = await Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
		return psbt
	}

	/** Zero-value signature request on the native-segwit payment key (offer replies). */
	async dummyBalancePsbt(): Promise<string> {
		return dummyNativeSegwitPsbt(this.balancePublicKey())
	}

	/** Each input paired with the OYL account that owns it; unowned inputs are left alone. */
	ownedSignInputs(tx: btc.Transaction): Array<{ index: number; address: string; publicKey: string }> {
		const accounts = [
			{ address: this.balanceAddress(), publicKey: this.balancePublicKey() },
			{ address: this.taprootAddress(), publicKey: this.taprootPublicKey() }
		]
		const byScript = new Map<string, { address: string; publicKey: string }>()
		for (const account of accounts) {
			if (!account.address) continue
			try {
				const script = btc.OutScript.encode(btc.Address().decode(account.address))
				byScript.set(Buffer.from(script).toString('hex'), account)
			} catch {}
		}
		const out: Array<{ index: number; address: string; publicKey: string }> = []
		for (const index of allInputIndexes(tx)) {
			const account = byScript.get(inputScriptHex(tx, index) ?? '')
			if (account) out.push({ index, ...account })
		}
		return out
	}

	async login(): Promise<void> {
		// @ts-ignore
		if (!window.oyl) {
			throw new Error('OYL is not installed. Install the extension, then reconnect.')
		}

		// @ts-ignore
		const network = await window.oyl.getNetwork()

		if (network !== 'mainnet') {
			// @ts-ignore
			await window.oyl.switchNetwork('mainnet')
		}

		// @ts-ignore
		const { taproot, nativeSegwit } = await window.oyl.getAddresses()

		localStorage.setItem(STORAGE_TAPROOT_ADDRESS, taproot.address)
		localStorage.setItem(STORAGE_XVERSE_TAPROOT_PUBLIC_KEY, taproot.publicKey)
		localStorage.setItem(STORAGE_WALLET_TYPE, 'oyl')
		localStorage.setItem(STORAGE_PUBLIC_KEY, nativeSegwit.publicKey)
		localStorage.setItem(STORAGE_XVERSE_PAYMENT_ADDRESS, nativeSegwit.address)
		try {
			await Wallet.fetchProfile()
		} catch (_e) {}
	}

	balancePublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}
	balanceAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_XVERSE_PAYMENT_ADDRESS)
	}

	taprootPublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_XVERSE_TAPROOT_PUBLIC_KEY)
	}
	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	async signPSBTS(psbts: string[]): Promise<string[]> {
		const options = psbts.map((psbt) => {
			const psbt_tx = parsePsbtTransaction(psbt)

			return {
				autoFinalized: true,
				// OYL holds two accounts. Sign each input with the account that owns
				// it; the old code named the taproot address with the segwit key for
				// every input, which is neither.
				toSignInputs: this.ownedSignInputs(psbt_tx)
			}
		})

		try {
			// @ts-ignore
			const data = await window.oyl.signPsbts(psbts, options)
			return data
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBT(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await window.oyl.signPsbt({ psbt, finalize: true, broadcast: false })
			return normalizePsbtHex(data.psbt)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBTExtractTx(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await window.oyl.signPsbt({ psbt, finalize: true, broadcast: false })
			return extractFinalizedPsbtHex(data.psbt)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signMessage(message: String): Promise<string> {
		try {
			// @ts-ignore
			const res = await window.oyl.signMessage(message, 'bip322-simple')
			return res
		} catch (_e) {
			return undefined as unknown as string
		}
	}

	async signInscribe(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signTransfer(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signEscrow(psbt: string): Promise<string> {
		return this.signPSBT(psbt)
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		try {
			let signedSetup = ''

			if (setup_psbt) {
				const signedSetupPSBT = await this.signPSBT(setup_psbt)

				signedSetup = extractFinalizedPsbtHex(signedSetupPSBT)
			}
			const signedPurchase = await this.signPSBT(purchase_psbt)

			return { signedSetup, signedPurchase }
		} catch (e) {
			if (isUserRejection(e)) {
				return undefined as unknown as { signedSetup: string; signedPurchase: string }
			}
			console.log(e)
			throw e
		}
	}
}
